import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, CalendarDays, CalendarClock, Users, ScrollText, Link2, Mail, Settings, LogOut, Menu, ExternalLink, MessageCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, testId: "nav-overview" },
  { to: "/dashboard/submissions", label: "Submissions", icon: Inbox, testId: "nav-submissions" },
  { to: "/dashboard/schedule", label: "Schedule", icon: CalendarDays, testId: "nav-schedule" },
  { to: "/dashboard/intake-links", label: "Intake links", icon: Link2, testId: "nav-intake-links" },
  { to: "/dashboard/availability", label: "Availability", icon: CalendarClock, supervisor: true, testId: "nav-availability" },
  { to: "/dashboard/users", label: "Team", icon: Users, supervisor: true, testId: "nav-users" },
  { to: "/dashboard/emails", label: "Email log", icon: Mail, supervisor: true, testId: "nav-emails" },
  { to: "/dashboard/whatsapp", label: "WhatsApp", icon: MessageCircle, supervisor: true, testId: "nav-whatsapp" },
  { to: "/dashboard/audit", label: "Audit log", icon: ScrollText, supervisor: true, testId: "nav-audit" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

function NavItems({ isSupervisor, onNavigate }) {
  return (
    <nav className="flex flex-1 flex-col gap-1" aria-label="Main">
      {NAV.filter((n) => !n.supervisor || isSupervisor).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={n.testId} className={cn("nav-item")}>
          <n.icon className="h-[18px] w-[18px]" aria-hidden="true" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="rounded-[10px] bg-[hsl(var(--app-navy-deep))] px-4 py-3">
      <img src="/brand/emerge-logo-white.png" alt="EMERGE" className="h-7 w-auto" />
      <p className="mt-1 text-[10px] tracking-wide text-white/65">Therapy Center - Intake dashboard</p>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout, isSupervisor } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-dvh bg-background lg:grid lg:grid-cols-[var(--sidebar-width)_1fr]">
      <aside className="hidden border-e border-border bg-white lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col lg:gap-6 lg:p-4" data-testid="dashboard-sidebar">
        <Brand />
        <NavItems isSupervisor={isSupervisor} />
        <div className="border-t border-border pt-4">
          <a href="/intake" target="_blank" rel="noreferrer" className="nav-item text-sm" data-testid="nav-open-intake">
            <ExternalLink className="h-4 w-4" /> Open client form
          </a>
        </div>
      </aside>

      <div className="flex min-h-dvh flex-col">
        <header className="sticky top-0 z-20 flex h-[var(--topbar-height)] items-center justify-between border-b border-border bg-white/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-white/80 lg:px-8" data-testid="dashboard-topbar">
          <div className="flex items-center gap-3">
            <button type="button" className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] border border-border bg-white lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </button>
            <span className="font-heading text-sm font-semibold text-muted-foreground lg:hidden">EMERGE</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-end sm:block">
              <p className="text-sm font-semibold leading-tight text-foreground" data-testid="topbar-user-name">{user?.name}</p>
              <p className="text-xs capitalize text-muted-foreground" data-testid="topbar-user-role">{user?.role}</p>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: user?.color || "#2f5d83" }} aria-hidden="true">
              {initials}
            </div>
            <button type="button" onClick={onLogout} className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-border bg-white px-3 text-sm font-medium text-foreground hover:bg-secondary" data-testid="logout-button">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-6 lg:px-8">
          <Outlet />
        </main>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[300px] bg-white p-4">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="flex h-full flex-col gap-6">
            <Brand />
            <NavItems isSupervisor={isSupervisor} onNavigate={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
